import { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/EditOutlined";
import Logistics from "../../../../../components/logistics";
import ILocation from "../../../../../types/ILocation";
import { useNitroStore } from "../../../../../store/Store";
import CloseIcon from "../../../../../components/CloseIcon";
import UnsavedConfirmation from "../../../../../components/unsavedConfirmation";
import PanelFooter from "../../../../../components/Panel/PanelFooter";
import LoadingIcon from "../../../../../components/Loading";
import Array from "../../../../../utils/array";
import { useNitroCAStore } from "../../../../../store/CompetitiveAnalysisStore";
import { convertModeParams } from "../../../../multiLocationQuotePanel/components/multiLocationQuoteUtils";
import { useIsMobile } from "../../../../../hooks/useIsMobile";
import { toModes } from "../../../../../utils/utils";
import { saveLocationModes } from "../../../../../utils/railStationLocationApi";
import { hasRequiredRailStationAndCarriers } from "../../../../../utils/logisticsValidation";
import { IStationRow } from "../../../../../types/IRailFreightDetails";

const OverviewLogisticsTab = ({
  location,
  reloadLocation,
}: {
  location: ILocation;
  reloadLocation: () => Promise<void>;
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [
    savedNotification,
    failedNotification,
    insightsEditing,
    setInsightsEditing,
    setFetchFavoriteOrigins,
    closeInsightsPanel,
    setLogisticsUpdatedModes,
    pendingRailStationUpdate,
    setPendingRailStationUpdate,
    featureFlags,
  ] = useNitroStore((state) => [
    state.savedNotification,
    state.failedNotification,
    state.insightsEditing,
    state.setInsightsEditing,
    state.setFetchFavoriteOrigins,
    state.closeInsightsPanel,
    state.setLogisticsUpdatedModes,
    state.pendingRailStationUpdate,
    state.setPendingRailStationUpdate,
    state.featureFlags,
  ]);
  const [
    expandedDestinationId,
    setExpandedDestination,
    troubleshootQuoteOpen,
    triggerRefetchMultiLocation,
    setInsightsPanelSaved,
  ] = useNitroCAStore((state) => [
    state.expandedDestId,
    state.setExpandedDestination,
    state.troubleshootQuoteOpen,
    state.triggerRefetchMultiLocation,
    state.setInsightsPanelSaved,
  ]);

  const isMobile = useIsMobile();
  const modes = toModes(location.inboundModes, location.outboundModes);

  const [temporaryLocation, setTemporaryLocation] = useState<
    ILocation | undefined
  >(location);
  const [railStations, setRailStations] = useState<IStationRow[]>([]);
  useEffect(() => {
    setTemporaryLocation(location);
    setRailStations([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (
      !pendingRailStationUpdate ||
      pendingRailStationUpdate.locationId !== location.id ||
      pendingRailStationUpdate.targetRowId
    )
      return;
    setTemporaryLocation((prev) => ({
      ...(prev ?? location),
      railStation: pendingRailStationUpdate.stationName,
      stationState: pendingRailStationUpdate.stationState,
      carriers: pendingRailStationUpdate.carriers,
      scrsId: pendingRailStationUpdate.scrsId,
    }));
    setPendingRailStationUpdate(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRailStationUpdate]);

  const updateLogistics = async () => {
    if (!temporaryLocation) return;
    try {
      setIsSaving(true);
      await saveLocationModes({
        id: temporaryLocation.id,
        modes: convertModeParams(
          toModes(
            temporaryLocation.inboundModes,
            temporaryLocation.outboundModes,
          ),
        ),
        threeLegsAllowed: temporaryLocation.threeLegsAllowed || false,
        equipmentTypes: temporaryLocation.equipmentTypes || [],
        multiRailStation: featureFlags.multiRailStation,
        railStations,
        legacyRailStation: !isMobile
          ? {
              scrsId: temporaryLocation.scrsId || "",
              stationState:
                temporaryLocation.scrsId || !temporaryLocation.stationState
                  ? ""
                  : temporaryLocation.stationState,
              carriers: temporaryLocation.carriers.map((c) => c.id),
            }
          : undefined,
      });

      if (!isMobile) {
        await reloadLocation();
      }

      setFetchFavoriteOrigins(true);
      setInsightsEditing(undefined);
      if (isMobile || troubleshootQuoteOpen) {
        if (troubleshootQuoteOpen) {
          setInsightsPanelSaved(true);
        }
        closeInsightsPanel();
      }
      savedNotification("Successfully updated logistics.");
      setLogisticsUpdatedModes({
        destId: temporaryLocation.id,
        modes: toModes(
          temporaryLocation.inboundModes,
          temporaryLocation.outboundModes,
        ),
      });
      triggerRefetchMultiLocation();

      if (expandedDestinationId) {
        setExpandedDestination({ id: expandedDestinationId, name: "" });
      }
    } catch (err) {
      console.error(err);
      failedNotification("Failed to update logistics.", (err as any).message);
    }

    setIsSaving(false);
  };

  const [closeConfirmationOpen, setCloseConfirmationOpen] = useState(false);
  const [hasDirtyAdditionalRailRow, setHasDirtyAdditionalRailRow] =
    useState(false);

  // ################################################################################################################################
  // ## VALIDATION ##################################################################################################################
  // ################################################################################################################################
  const isValid = useMemo(() => {
    if (!temporaryLocation) return false;

    return hasRequiredRailStationAndCarriers(temporaryLocation);
  }, [temporaryLocation]);

  const hasChange = useMemo(() => {
    if (!temporaryLocation || !location) return true;

    let newhasChange = false;

    const {
      inboundModes,
      outboundModes,
      carriers,
      stationState,
      equipmentTypes,
      threeLegsAllowed,
    } = temporaryLocation;
    const {
      inboundModes: originalInboundModes,
      outboundModes: originalOutboundModes,
      carriers: originalCarriers,
      stationState: originalRailStation,
      equipmentTypes: originalEquipmentTypes,
      threeLegsAllowed: originalThreeLegsAllow,
    } = location;
    const originalModes = toModes(originalInboundModes, originalOutboundModes);
    const newModes = toModes(inboundModes, outboundModes);

    if (stationState !== originalRailStation) newhasChange = true;
    if (
      Array.compareStringList(
        originalEquipmentTypes || [],
        equipmentTypes || [],
      )
    ) {
      newhasChange = true;
    }
    if (carriers.length !== originalCarriers.length) {
      newhasChange = true;
    } else {
      carriers.forEach((carrier, index) => {
        const originalCarrier = originalCarriers[index];

        if (!originalCarrier) {
          newhasChange = true;
        } else if (carrier !== originalCarrier) {
          newhasChange = true;
        }
      });
    }

    if (newModes.TRUCK.inbound !== originalModes.TRUCK.inbound)
      newhasChange = true;
    if (newModes.TRUCK.outbound !== originalModes.TRUCK.outbound)
      newhasChange = true;

    if (newModes.RAIL.inbound !== originalModes.RAIL.inbound)
      newhasChange = true;
    if (newModes.RAIL.outbound !== originalModes.RAIL.outbound)
      newhasChange = true;

    if (newModes.VESSEL.inbound !== originalModes.VESSEL.inbound)
      newhasChange = true;
    if (newModes.VESSEL.outbound !== originalModes.VESSEL.outbound)
      newhasChange = true;

    if (newModes.BARGE.inbound !== originalModes.BARGE.inbound)
      newhasChange = true;
    if (newModes.BARGE.outbound !== originalModes.BARGE.outbound)
      newhasChange = true;

    if (newModes.PIPELINE.inbound !== originalModes.PIPELINE.inbound)
      newhasChange = true;
    if (newModes.PIPELINE.outbound !== originalModes.PIPELINE.outbound)
      newhasChange = true;

    if (threeLegsAllowed !== originalThreeLegsAllow) {
      newhasChange = true;
    }

    return newhasChange;
  }, [temporaryLocation, location]);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {closeConfirmationOpen && (
        <UnsavedConfirmation
          confirm={() => {
            setCloseConfirmationOpen(false);
            setTemporaryLocation(location);
            setInsightsEditing(undefined);
            if (troubleshootQuoteOpen) {
              closeInsightsPanel();
            }
          }}
          cancel={() => {
            setCloseConfirmationOpen(false);
          }}
        />
      )}
      <div
        className="insightsPanelPadding"
        style={{
          height: 32.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 10,
        }}
      >
        <div
          style={{
            color: "#0F2C52",
            fontWeight: 700,
            fontSize: 18,
            textTransform: "uppercase",
          }}
        >
          Logistics
        </div>
        {insightsEditing !== "Logistics" && (
          <Button
            id="logisticsEditButton"
            variant="contained"
            onClick={() => {
              setInsightsEditing("Logistics");
            }}
            style={{ display: "flex", gap: 4, zIndex: 9999 }}
          >
            <EditIcon style={{ fontSize: 18 }} />
            Edit
          </Button>
        )}
        {!isSaving && insightsEditing === "Logistics" && (
          <span id="edit-close-button">
            <CloseIcon
              onClick={() => {
                if (hasChange) {
                  setCloseConfirmationOpen(true);
                } else {
                  setInsightsEditing(undefined);
                  if (troubleshootQuoteOpen) {
                    closeInsightsPanel();
                  }
                }
              }}
            />
          </span>
        )}
      </div>

      {modes && (
        <div
          className="insightsPanelPadding"
          style={{
            flexGrow: 1,
            overflowY: "auto",
          }}
        >
          {!isSaving && temporaryLocation && (
            <div
              style={{
                border: "1px solid lightgray",
                borderRadius: 4,
                display: "flex",
                flexDirection: "column",
                padding: "8px 0px",
                gap: 8,
              }}
            >
              <Logistics
                originalLocation={location}
                location={temporaryLocation}
                setLocation={setTemporaryLocation}
                disabled={insightsEditing !== "Logistics"}
                onAdditionalRowsDirtyChange={setHasDirtyAdditionalRailRow}
                onRailStationsChange={setRailStations}
              />
            </div>
          )}
          {isSaving && temporaryLocation && (
            <div
              style={{
                height: "100%",
                flexGrow: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <LoadingIcon color="#005CA6" height={100} width={100} />
            </div>
          )}
        </div>
      )}
      {insightsEditing === "Logistics" && (
        <PanelFooter>
          <Button
            id="logisticsSaveButton"
            disabled={(!hasChange && !hasDirtyAdditionalRailRow) || !isValid}
            variant="contained"
            fullWidth
            onClick={updateLogistics}
          >
            SAVE
          </Button>
        </PanelFooter>
      )}
    </div>
  );
};

export default OverviewLogisticsTab;
